import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { AlarmClock, Archive, ArrowUpFromLine, ArchiveRestore, BellOff, BellRing, Bot, ClipboardCopy, FileQuestion, GitMerge, Link2, ListTree, MessagesSquare, MoreHorizontal, Play, Repeat, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ConfirmDialog';
import { sendsBack } from '@shared/factory-dispute';
import type { RunSummary } from '@shared/types/factory';
import type { TaskBoard, TaskCard, TaskContextRef, TaskDetail, TaskPushSource, TaskUpdateInput } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DialogClose } from '@/components/ui/dialog';
import { StatusSelect } from '@/components/StatusSelect';
import type { StatusMeta } from '@/components/status-tone';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AppAlert } from '@/components/AppAlert';
import { EmptyState } from '@/components/EmptyState';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { SnoozeMenu } from '@/components/SnoozeMenu';
import { isSnoozed, snoozeDay } from '@/lib/snooze';
import { addProposedSubtasks, declineTaskSuggestions, makeTaskSubtaskOf, promoteSubtaskToTask, decideTaskSuggestions, settleTaskDispute, sendDecisionsUpstream, sendCommentUpstream, getUpstreamPeople, pushTaskUpstream, reorderTaskSubtasks, suggestSubtaskOrder, acceptSubtaskOrder, dismissSubtaskOrder, markTaskNotificationsRead, moveTask, setTaskFollowing, summariseTask, updateTask } from '@/lib/tasks-api';
import { TaskAiSummary } from './TaskAiSummary';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useIsAdmin, usePermission } from '@/hooks/usePermission';
import { useChatAboutTask } from '@/pages/chat/chat-panel-state';
import { useCurrentProject, useFormatTaskRef } from '@/lib/current-project';
import { runPath, taskPath } from '@/navigation/project-paths';
import { RunTaskDialog } from '@/components/factory/RunTaskDialog';
import { TaskRunsList } from '@/components/factory/TaskRunsList';
import { TaskUsageCard } from '@/components/factory/TaskUsageCard';
import { useWorkUsage } from '@/components/factory/use-work-usage';
import { activeLiveRun, continuableRun, isRunActive, runNumberFromActorLabel, runRef } from '@/components/factory/run-status';
import { WrapUpButton } from '@/components/factory/WrapUpButton';
import { TaskDevServersPanel } from '@/components/factory/DevServers';
import { useTaskDevServers } from '@/components/factory/use-task-dev-servers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { runsByNumber, useTaskRuns } from '@/components/factory/use-task-runs';
import { describeApiError } from '@/lib/api-errors';
import { cancelRun, retryRunSetup } from '@/lib/runs-api';
import { TaskAttachments } from './TaskAttachments';
import { TaskAttachmentViewerProvider } from './TaskAttachmentViewer';
import { TaskDecisions } from './TaskDecisions';
import { TaskSuggestions } from './TaskSuggestions';
import { TaskToAnswer } from './TaskToAnswer';
import { TaskApprovalRequestsSection } from './TaskApprovalRequests';
import { TaskPatchRequestsSection } from './TaskPatchRequests';
import { ProposedSubtasks } from './ProposedSubtasks';
import { TaskSubtasks, type SubtaskBuild } from './TaskSubtasks';
import { isSubtaskBuilt, isSubtaskDone, reachedBreaks, stackableSubtasks, tickColumn, waitsOnAi, withAiBuilder, withOrderSuggestion, subtasksToBuild } from './subtasks';
import { asWaitingRef, rollUpSubtaskWait } from './task-waiting';
import { TaskActiveRunsContext, useProjectActiveRuns } from '@/components/factory/active-runs';
import { stackedMergedText } from './stacked-on';
import { TaskDescription } from './TaskDescription';
import { TaskSpec } from './TaskSpec';
import { TaskProperties } from './TaskProperties';
import { ShowChangesToggle, TaskTimeline } from './TaskTimeline';
import { AgentRunLine, TaskAgentPane } from './TaskAgentPane';
import { TaskChatComposer } from './TaskChatComposer';
import { usePendingSubtasks } from './pending-subtasks';
import { MakeSubtaskDialog } from './MakeSubtaskDialog';
import { PushUpstreamDialog } from './PushUpstreamDialog';
import { TaskPrActionButtons, TaskPrActionMenuItems } from './TaskPrActions';
import { useTaskPrActions, type TaskAction } from './use-task-prs';
import { mergeRunNote } from './merge-run-warning';
import { headerActionLabel, headerPrAction, planMoveActions } from './task-pr-actions';
import { AiBuilderMentions } from './ai-builder-mention';
import { TaskRepeatDialog } from './TaskRepeatDialog';
import { repeatNextLabel } from './task-repeat-label';
import { describeTaskRepeat, type TaskRepeat } from '@shared/task-repeat';
import { useIsMobile } from '@/hooks/use-mobile';
import { RecordDialog } from '@/components/RecordDialog';
import { useUrlRecord } from '@/lib/url-state/use-url-record';
import { agentTabRun } from '@/components/factory/live-ui';
import { useScrollEnd } from '@/hooks/use-follow-scroll';
import { JumpToLatestButton } from '@/components/JumpToLatestButton';
import { useTaskDetail, useTaskMeta } from './useTasks';
import { optimisticUpdate } from '@/stores/taskOptimistic';
import { orderSuggestionCount } from '@shared/subtask-order';
import { isStaleWriteCode } from '@shared/types/stale-write';
import { useTaskStore } from '@/stores/taskStore';
import { compareRank, rankBetween } from '@shared/task-rank';
import { attachFilesToTask, sendMessage } from './task-files';
import { useFileDrop, usePreventFileNavigation } from '@/components/useFileDrop';
import { copyTasksMarkdown } from './copy-task-markdown';
import { copyWithToast } from '@/lib/clipboard';
import { TASK_SOURCE_LABELS, upstreamBadgeText } from '@shared/types/task-sources';
import { ExternalLink } from '@/components/ExternalLink';
import { UpstreamChip, UpstreamMark } from './task-chips';
import { TaskRequestor } from './TaskCardMeta';
import { BrokenLinkBadge } from './BrokenLink';
import { PaneResizeHandle } from '@/components/PaneResizeHandle';
import { usePref } from '@/hooks/use-pref';
import { UI_PREFS, withCodec } from '@/lib/ui-prefs';

/** The Board pop-up's activity pane: 24rem until it's dragged, at least 20rem, leaving the task 28rem. */
const ACTIVITY_MIN_WIDTH = 320;
const TASK_MIN_WIDTH = 448;
const ACTIVITY_WIDTH = withCodec(UI_PREFS.tasksActivityWidth, {
  defaultValue: 384,
  parse: (raw) => {
    const stored = Number(raw);
    return Number.isFinite(stored) && stored >= ACTIVITY_MIN_WIDTH ? Math.round(stored) : undefined;
  },
  serialize: (width) => String(width),
});

type BoardMeta = Pick<TaskBoard, 'members' | 'labels' | 'columns' | 'types' | 'push_sources'>;

/**
 * The Board's columns as statuses, for the column picker. Projects name their own columns, so the tone
 * comes from where a column sits: the first is not started, a done column is done, the rest are
 * under way.
 */
function taskColumnStatuses(columns: BoardMeta['columns']): Record<string, StatusMeta> {
  return Object.fromEntries(
    columns.map((c, i) => [
      c.id,
      { label: c.name, tone: c.is_done ? 'success' : i === 0 ? 'neutral' : 'info' } satisfies StatusMeta,
    ])
  );
}

/** The phone pop-up's pinned top bar: its close button (a 44px touch target) and its border. */
const PINNED_BAR_HEIGHT = 45;

type TaskDetailProps = {
  taskId: string;
  layout?: 'wide' | 'narrow';
  /** The page's own Context, offered first when linking. */
  currentPage?: (TaskContextRef & { title: string }) | null;
  /** Called when a link inside the task navigates away (so a modal can close). */
  onNavigate?: () => void;
  /** Told the task's number and title as they load and change (the link dialog's header, SF-T-160). */
  onShown?: (shown: { number: number | null; title: string } | null) => void;
};

/**
 * Everything about one task. `layout="wide"` for the Board modal, `"narrow"` for the Tasks panel. It
 * needs its Board's columns, labels, types and people: `meta` when the page has the Board (the Board,
 * a Tasks panel, the master board), else the tab's project's cached task meta (SF-T-172: the Inbox, the
 * link dialog), shown at once when cached.
 */
export function TaskDetailView({ meta, ...props }: TaskDetailProps & { meta?: BoardMeta }) {
  if (meta) return <TaskDetailBody {...props} meta={meta} />;
  return <TaskDetailFromCache {...props} />;
}

function TaskDetailFromCache(props: TaskDetailProps) {
  const loaded = useTaskMeta();
  const meta = loaded.data?.data;
  if (meta) return <TaskDetailBody {...props} meta={meta} />;
  if (loaded.data?.error) {
    return (
      <div className="p-6">
        <LoadError title="Couldn’t open this task" error={describeApiError(loaded.data)} onRetry={loaded.reload} />
      </div>
    );
  }
  return <LoadingState label="Loading the task…" />;
}

function TaskDetailBody({
  taskId,
  meta,
  layout = 'wide',
  currentPage,
  onNavigate,
  onShown,
}: TaskDetailProps & { meta: BoardMeta }) {
  const { task, hydrated, error, notFound, reload } = useTaskDetail(taskId);
  const shownNumber = task?.number ?? null;
  const shownTitle = task?.title ?? null;
  useEffect(() => {
    onShown?.(shownTitle === null ? null : { number: shownNumber, title: shownTitle });
  }, [onShown, shownNumber, shownTitle]);
  // SF-T-79: Merge and Fix conflicts for the task's open PRs (before the early returns: it's a hook).
  const prActions = useTaskPrActions(task ?? null);
  const formatRef = useFormatTaskRef();
  // SF-T-141: Chat (admins) from the ⋯ menu, which phones reach when the task covers the screen.
  const isAdmin = useIsAdmin();
  const chatAboutTask = useChatAboutTask();
  const isMobile = useIsMobile();
  // The activity pane's width: saved per browser when a drag ends, followed live while dragging.
  const [savedActivityWidth, saveActivityWidth] = usePref(ACTIVITY_WIDTH);
  const [draggedActivityWidth, setDraggedActivityWidth] = useState<number | null>(null);
  const activityWidth = draggedActivityWidth ?? savedActivityWidth;
  const boards = useTaskStore((s) => s.boards);
  const meId = useAuthStore((s) => s.user?.id);
  const project = useCurrentProject();
  // Maintainers start runs (admins are maintainers everywhere); the server checks too.
  const canRun = usePermission('RUNS_START');
  // T-27: a draft's Promote and Discard are edits.
  const canEditTasks = usePermission('TASKS_EDIT');
  const [runOpen, setRunOpen] = useState(false);
  // T-42: the sub-tasks picked to build together, while the Run dialog is open for them.
  const [buildIds, setBuildIds] = useState<string[] | null>(null);
  const navigate = useNavigate();
  // The task's runs, polled while one is going: the Runs list and the timeline's run links show them.
  const runsSlug = project && task?.number ? project.slug : null;
  const runsLoader = useTaskRuns(taskId, runsSlug, newestRunMentioned(task?.events));
  // Only this task's runs: the last task's stay in the loader while this one's load, and acting on them (Wrap up,
  // the Agent tab) would act on that task's run.
  const ownRuns = runsLoader.data?.taskId === taskId ? runsLoader.data : undefined;
  const runs = ownRuns?.data?.runs;
  const runsError = ownRuns?.error ? describeApiError(ownRuns) : null;
  // SF-T-209: the task's newest run, while it's live: Wrap up takes Run's place, and the Agent tab opens first.
  const liveRun = activeLiveRun(runs);
  // SF-T-237: Merge shows a warning icon and hint while a run is still going on the task (its dialog checks again).
  const mergeNote = mergeRunNote(runs?.find((r) => isRunActive(r.status)));
  const prActionList: TaskAction[] = mergeNote ? prActions.actions.map((a) => (a.kind === 'merge' ? { ...a, runNote: mergeNote } : a)) : prActions.actions;
  const [activityTab, setActivityTab] = useState<{ taskId: string; tab: 'comments' | 'agent' } | null>(null);
  // SF-T-266: on a phone, the agent's chat full screen over the task (?agent=chat; Back closes it), as T3 Code's app.
  const agentChat = useUrlRecord('agent');
  const agentRun = agentTabRun(runs);
  // SF-T-266: the run whose thread the full-screen chat sees at work, so its title says Working while the run waits.
  const [threadWorkingRun, setThreadWorkingRun] = useState<string | null>(null);
  // Chosen once per task, when its runs have loaded, so the tab someone is reading doesn't switch when the run
  // stops being live (or when the runs arrive after the first paint). Not on the last task's runs, still on screen.
  if (runs && activityTab?.taskId !== taskId) setActivityTab({ taskId, tab: liveRun ? 'agent' : 'comments' });
  const shownTab = activityTab?.taskId === taskId ? activityTab.tab : 'comments';
  // SF-T-209: the worktree of the task's newest run that has one, and its dev servers.
  const hasWorktree = Boolean(runs?.some((r) => r.worktree_path));
  const devServers = useTaskDevServers(taskId, hasWorktree ? runsSlug : null, `${task?.updated_at ?? ''}:${runs?.[0]?.status ?? ''}`);
  // T-92: the project's runs going, for the stripe on open sub-tasks' rows (only while it has some).
  const hasOpenSubtasks = Boolean(task && !task.parent && task.subtask_cards.some((s) => !isSubtaskDone(s, meta.columns)));
  const subtaskRuns = useProjectActiveRuns(runsSlug && hasOpenSubtasks ? runsSlug : '');
  /**
   * T-58: on a mirrored task, sent questions and suggestions say so; with the source on, people who can
   * comment send them there ("Send to Asana").
   */
  const upstreamDecisions = (kind: 'questions' | 'suggestions') => {
    const upstream = task?.upstream;
    if (!task || !upstream) return undefined;
    const sourceLabel = TASK_SOURCE_LABELS[upstream.kind];
    const onSend =
      upstream.enabled && canEditTasks
        ? async (eventId: string, numbers: number[], mentionId: string | null) => {
            const res = await sendDecisionsUpstream(task.id, eventId, kind, numbers, mentionId);
            if (!res.data) {
              toast.error(describeApiError(res, `Couldn't send them to ${sourceLabel}`));
              return false;
            }
            if (!res.data.sent) {
              toast.error(`Couldn't post to ${sourceLabel}`, { description: res.data.upstream_error ?? undefined });
              return false;
            }
            if (res.data.upstream_error) {
              toast.warning(`Sent to ${sourceLabel}, but it didn't confirm the comment arrived. Check the task there.`, { description: res.data.upstream_error });
            } else toast.success(`Sent to ${sourceLabel}; the task waits on ${sourceLabel} for a reply`);
            void reload();
            return true;
          }
        : undefined;
    // SF-T-101: who the send can tag (its creator there, ticked to start), read when the dialog opens.
    const tagging = onSend ? { key: `${project?.slug ?? 'none'}:upstream-people:${task.id}`, load: (signal: AbortSignal) => getUpstreamPeople(task.id, { signal }) } : undefined;
    return { sourceLabel, waiting: task.waiting_on.some((w) => w.target === 'upstream'), onSend, tagging };
  };
  // SF-T-201: Send to <source> on a comment, on a task mirrored from an enabled source you can edit.
  const commentSend = (() => {
    const upstream = task?.upstream;
    if (!task || !upstream?.enabled || !canEditTasks) return undefined;
    const sourceLabel = TASK_SOURCE_LABELS[upstream.kind];
    return {
      sourceLabel,
      tagging: { key: `${project?.slug ?? 'none'}:upstream-people:${task.id}`, load: (signal: AbortSignal) => getUpstreamPeople(task.id, { signal }) },
      onSend: async (eventId: string, mentionId: string | null) => {
        const res = await sendCommentUpstream(task.id, eventId, mentionId);
        if (!res.data) {
          toast.error(describeApiError(res, `Couldn't send it to ${sourceLabel}`));
          return false;
        }
        if (!res.data.sent) {
          toast.error(`Couldn't post to ${sourceLabel}`, { description: res.data.upstream_error ?? undefined });
          void reload();
          return false;
        }
        if (res.data.upstream_error) {
          toast.warning(`Sent to ${sourceLabel}, but it didn't confirm the comment arrived. Check the task there.`, { description: res.data.upstream_error });
        } else toast.success(`Sent to ${sourceLabel}`);
        void reload();
        return true;
      },
    };
  })();
  // A run started here keeps you on the task (T-64): the Runs list loads it now and highlights it
  // once it's there, and the toast opens it.
  const [startedRun, setStartedRun] = useState<number | null>(null);
  const runStarted = (slug: string, number: number) => {
    setStartedRun(number);
    runsLoader.reload();
    toast.success(`Started ${runRef(number)}`, {
      action: {
        label: 'Open run',
        onClick: () => {
          onNavigate?.();
          navigate(runPath(slug, number));
        },
      },
    });
  };
  // Loaded again when the newest run changes state: its T3 runs arrive once they finish.
  const usage = useWorkUsage(runsSlug ? { task_id: taskId } : null, runsSlug, runs?.[0] ? `${runs[0].id}:${runs[0].status}` : null);
  // A title draft exists only while typing; otherwise the task's own title shows (live updates
  // included). It remembers its task so switching tasks never carries it over.
  const [titleDraft, setTitleDraft] = useState<{ taskId: string; text: string } | null>(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [repeatOpen, setRepeatOpen] = useState(false);
  // SF-T-132: "Make it a sub-task of…".
  const [makeSubOpen, setMakeSubOpen] = useState(false);
  // SF-T-207: "Push to source…".
  const [pushOpen, setPushOpen] = useState(false);
  const [summarising, setSummarising] = useState(false);
  // SF-T-271: the AI is suggesting the sub-tasks' order.
  const [suggestingOrder, setSuggestingOrder] = useState(false);
  // Phones' pop-up (T-49): once the title has scrolled up under the pinned bar, the bar shows it on one line.
  const [titleOutOfView, setTitleOutOfView] = useState(false);
  const watchTitle = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    // Watched against the pop-up's scrolling box, less the bar; out of view below doesn't count.
    const observer = new IntersectionObserver(
      ([entry]) => setTitleOutOfView(!entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0)),
      { root: el.closest('[data-slot="dialog-content"]'), rootMargin: `-${PINNED_BAR_HEIGHT}px 0px 0px 0px` }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // SF-T-222: phones: Jump to latest over the pinned chat box while the comments are in view but not at their end.
  const commentsEnd = useRef<HTMLDivElement>(null);
  const commentsJump = useScrollEnd(commentsEnd, isMobile && shownTab === 'comments', task?.id);
  // Files dropped anywhere on the task are posted to it (the chat box keeps its own drops).
  const drop = useFileDrop((files) => void attachFilesToTask(taskId, files));
  usePreventFileNavigation();
  // SF-T-144: sub-tasks added here show at once and are kept in the browser until the server has them.
  const pendingSubtasks = usePendingSubtasks(taskId, task?.subtask_cards.map((s) => s.id) ?? [], reload);

  // The task closing or remounting mid-edit (the chat docking beside the pop-up, before focus leaves
  // the title) still saves the title, as the description does (SF-T-140).
  const saveTitleOnClose = useEffectEvent(() => {
    if (!task || titleDraft?.taskId !== task.id) return;
    const next = titleDraft.text.trim();
    if (next && next !== task.title) void optimisticUpdate(task.id, { fields: { title: next } }, updateTask(task.id, { title: next }));
  });
  useEffect(() => () => saveTitleOnClose(), []);

  useEffect(() => {
    void markTaskNotificationsRead({ task_id: taskId });
  }, [taskId]);

  if (!task) {
    // The same words as a record page (RecordPageState); the pop-up or panel is the way back.
    if (notFound) {
      return (
        <div className="p-4">
          <EmptyState
            icon={<FileQuestion className="size-5" />}
            title="This task doesn't exist"
            description="It may have been deleted, or the link is wrong."
          />
        </div>
      );
    }
    return error ? (
      <div className="p-4">
        <LoadError title="Couldn't load this task" error={error} onRetry={() => void reload()} />
      </div>
    ) : (
      <LoadingState label="Loading task…" />
    );
  }

  /**
   * T-92: a sub-task's Waiting on, column or archiving changes its parent's roll-up (whose turn its
   * card shows); this shows it there at once too.
   */
  const parentRollUp = (parentId: string, sub: TaskCard, change: Partial<TaskCard>) => {
    if (!('waiting_on' in change || 'column_id' in change || 'archived' in change)) return undefined;
    const next = { ...sub, ...change };
    const open = !next.archived && !meta.columns.find((c) => c.id === next.column_id)?.is_done;
    return [
      {
        taskId: parentId,
        card: (parent: TaskCard) => ({
          ...parent,
          subtask_waiting: rollUpSubtaskWait(parent.subtask_waiting ?? [], { task_id: sub.id, number: sub.number }, open, next.waiting_on),
        }),
      },
    ];
  };

  // Every change shows at once; the request confirms it or undoes it (see taskOptimistic).
  const run = (request: Promise<{ error?: string }>, fields?: Partial<TaskDetail>) =>
    optimisticUpdate(task.id, { fields, cards: task.parent && fields ? parentRollUp(task.parent.task_id, task, fields) : undefined }, request);

  const patch = (input: TaskUpdateInput, fields?: Partial<TaskDetail>) => run(updateTask(task.id, input), fields);

  /** A description save answers with its response: a stale one is shown by TaskDescription's banner, not a toast. */
  const saveDescription = async (input: TaskUpdateInput, fields?: Partial<TaskDetail>) => {
    const request = updateTask(task.id, input);
    await optimisticUpdate(task.id, { fields }, request, { quiet: (res) => isStaleWriteCode(res.code) });
    return request;
  };

  /** Moving from here puts the card at the bottom of its new column, as the server will. */
  const moveTo = (columnId: string) => {
    const ranks = Object.values(boards).flatMap((b) =>
      (b.data?.tasks ?? []).filter((t) => t.column_id === columnId && t.id !== task.id).map((t) => t.rank)
    );
    const last = ranks.sort(compareRank).at(-1) ?? null;
    const done = meta.columns.find((c) => c.id === columnId)?.is_done;
    void run(moveTask(task.id, { column_id: columnId }), {
      column_id: columnId,
      rank: rankBetween(last, null),
      done_at: done ? task.done_at ?? new Date().toISOString() : null,
      // Another column ends a snooze, as the server does.
      ...(columnId !== task.column_id ? { snoozed_until: null } : {}),
    });
  };

  // Snoozed: off the Board until then; it comes back in the same column.
  const snoozed = isSnoozed(task.snoozed_until);
  const snooze = (until: Date | null) => {
    const at = until ? until.toISOString() : null;
    void patch({ snoozed_until: at }, { snoozed_until: at }).then((ok) => {
      if (ok) toast.success(until ? `Snoozed until ${snoozeDay(until)}` : 'Back on the Board');
    });
  };

  // Repeat: it comes back on a schedule, done or not.
  const repeatText = task.repeat ? describeTaskRepeat(task.repeat) : null;
  const saveRepeat = (repeat: TaskRepeat | null, nextAt: Date | null) => {
    setRepeatOpen(false);
    const next_at = nextAt ? nextAt.toISOString() : null;
    void patch({ repeat }, { repeat, repeat_next_at: next_at }).then((ok) => {
      if (ok) toast.success(repeat ? `${describeTaskRepeat(repeat)}: next back ${repeatNextLabel(nextAt!)}` : 'It won’t repeat any more');
    });
  };

  // Sub-tasks (T-42): each change shows at once on this task's list; the live update brings the rest.
  const subtaskChange = (subId: string, change: Partial<TaskDetail['subtask_cards'][number]>, request: Promise<{ error?: string }>) => {
    const sub = task.subtask_cards.find((s) => s.id === subId);
    return optimisticUpdate(
      task.id,
      {
        detail: (t) => ({ ...t, subtask_cards: t.subtask_cards.map((s) => (s.id === subId ? { ...s, ...change } : s)) }),
        cards: sub ? parentRollUp(task.id, sub, change) : undefined,
      },
      request
    );
  };
  const toggleSubtaskDone = (sub: TaskCard, done: boolean) => {
    const to = tickColumn(meta.columns, done);
    if (to) void subtaskChange(sub.id, { column_id: to.id }, moveTask(sub.id, { column_id: to.id }));
  };
  const handSubtaskToAi = (sub: TaskCard) =>
    void subtaskChange(sub.id, { waiting_on: [...sub.waiting_on, { target: 'ai' }] }, updateTask(sub.id, { waiting_on: withAiBuilder(sub.waiting_on) })).then(
      (ok) => ok && toast.success(`${formatRef(sub.number)} is with the AI builder`)
    );
  const toggleSubtaskBoard = (sub: TaskCard, show: boolean) =>
    void subtaskChange(sub.id, { show_on_board: show }, updateTask(sub.id, { show_on_board: show }));
  // SF-T-268: what a sub-task waits on (its After… picker); a loop made meanwhile is refused and undone, and the picker
  // stays open with its ticks.
  const changeSubtaskAfter = (sub: TaskCard, ids: string[], was?: string[]) =>
    subtaskChange(
      sub.id,
      { after: ids.flatMap((id) => task.subtask_cards.filter((s) => s.id === id).map((s) => ({ task_id: s.id, number: s.number }))) },
      updateTask(sub.id, { after: ids, ...(was ? { after_was: was } : {}) })
    );
  // SF-T-269: the chain goes on past the breaks it stopped at: the AI builder takes this task again (in place of
  // whoever presses it), and the server marks the breaks it reached passed; the pick stays.
  const continueChain = () => {
    const reached = reachedBreaks(task.subtask_cards, meta.columns).map((s) => s.id);
    // Off whoever it stopped for, and whoever presses it: it's the AI builder's turn again.
    const waiting = task.waiting_on.filter((w) => !(w.target === 'user' && (w.user_id === meId || w.user_id === task.subtasks_chain_stopped_for)));
    void patch(
      { waiting_on: withAiBuilder(waiting) },
      {
        waiting_on: [...waiting.filter((w) => w.target !== 'ai'), { target: 'ai' }],
        subtask_cards: task.subtask_cards.map((s) => (reached.includes(s.id) ? { ...s, chain_break_passed_at: new Date().toISOString() } : s)),
        subtasks_chain_stopped_for: null,
      }
    ).then((ok) => ok && toast.success('The AI builder goes on from the break'));
  };
  // SF-T-271: Suggest the order: the AI's suggestion shows on the list as soon as it's back (it's kept on the task too).
  const suggestOrder = async () => {
    setSuggestingOrder(true);
    const res = await suggestSubtaskOrder(task.id);
    setSuggestingOrder(false);
    if (res.error || !res.data) {
      toast.error(res.error ? describeApiError(res, 'The AI couldn’t suggest an order') : 'The AI couldn’t suggest an order');
      return;
    }
    const suggestion = res.data.suggestion;
    void optimisticUpdate(task.id, { fields: { subtasks_suggestion: suggestion } }, Promise.resolve({}));
    if (!orderSuggestionCount(suggestion)) toast.success('The AI suggests keeping the order as it is');
  };
  // SF-T-271: every change at once, as the server applies them; a change to the sub-tasks meanwhile refuses it.
  const acceptOrder = () => {
    const suggestion = task.subtasks_suggestion;
    if (!suggestion) return;
    void optimisticUpdate(
      task.id,
      { detail: (t) => ({ ...t, subtasks_suggestion: null, subtask_cards: withOrderSuggestion(t.subtask_cards, suggestion) }) },
      acceptSubtaskOrder(task.id, suggestion.id)
    ).then((ok) => {
      const n = orderSuggestionCount(suggestion);
      if (ok) toast.success(`Applied the AI’s order: ${n} change${n === 1 ? '' : 's'}`);
    });
  };
  const dismissOrder = () => {
    const suggestion = task.subtasks_suggestion;
    if (suggestion) void optimisticUpdate(task.id, { fields: { subtasks_suggestion: null } }, dismissSubtaskOrder(task.id, suggestion.id));
  };
  // SF-T-269: Stop here for a person, or not (setting it again on a passed one stops the chain there again). The server
  // hands the parent back to the AI builder when the break its chain stopped at is taken off, as Continue does.
  const toggleSubtaskBreak = (sub: TaskCard, on: boolean) =>
    void subtaskChange(sub.id, { chain_break: on, chain_break_passed_at: null }, updateTask(sub.id, { chain_break: on }));
  const reorderSubtasks = (ids: string[]) =>
    void optimisticUpdate(
      task.id,
      { detail: (t) => ({ ...t, subtask_cards: ids.flatMap((id) => t.subtask_cards.filter((s) => s.id === id)) }) },
      reorderTaskSubtasks(task.id, ids)
    );
  // SF-T-147: off this task's list at once; it shows on the Board as a task of its own.
  const promoteSubtask = (sub: TaskCard) =>
    void optimisticUpdate(
      task.id,
      { detail: (t) => ({ ...t, subtask_cards: t.subtask_cards.filter((s) => s.id !== sub.id) }) },
      promoteSubtaskToTask(sub.id)
    ).then((ok) => {
      if (!ok) return;
      toast.success(`${formatRef(sub.number)} is a task of its own now`);
      void reload();
    });
  // SF-T-242: how the AI builder builds the sub-tasks, and Build when it's one at a time: the AI builder takes this task.
  const changeSubtaskBuild = (change: Partial<SubtaskBuild>) => {
    // SF-T-263: a batch going on the sub-tasks isn't changed by the setting: say so, and offer to cancel it.
    const batch = change.subtasks_one_at_a_time ? runs?.find((r) => isRunActive(r.status) && r.task?.id === task.id && r.subtasks.length > 0) : undefined;
    void patch(change, change).then((ok) => ok && batch && void offerToCancelBatch(batch));
  };
  const offerToCancelBatch = async (batch: RunSummary) => {
    const ref = runRef(batch.number);
    const ok = await confirmDialog({
      title: `${ref} is building them in one batch`,
      description: `${ref} builds ${batch.subtasks.length} of the sub-tasks together, on one branch. One at a time applies from the next build: ${ref} goes on as it started unless you cancel it.`,
      confirmLabel: `Cancel ${ref}`,
      cancelLabel: 'Keep it running',
      destructive: true,
    });
    if (!ok || !project) return;
    const res = await cancelRun(batch.id, { project: project.slug });
    if (res.data || res.code === 'RUN_FINISHED') toast.success(res.data ? `${ref} is cancelled` : `${ref} had already finished`);
    else toast.error(`Couldn't cancel ${ref}: ${describeApiError(res)}`);
    runsLoader.reload();
  };
  // SF-T-263: no pick, and off the AI builder, so the chain doesn't widen to every open sub-task.
  const clearChain = () =>
    void patch(
      { subtasks_chain: [], waiting_on: task.waiting_on.filter((w) => w.target !== 'ai').map(asWaitingRef) },
      { waiting_on: task.waiting_on.filter((w) => w.target !== 'ai'), subtask_cards: task.subtask_cards.map((s) => ({ ...s, in_chain: false })) }
    ).then((ok) => ok && toast.success('The pick is cleared'));
  // SF-T-263: Pick… one at a time: the picked ones are the chain, and the AI builder takes this task.
  const pickChain = (ids: string[]) =>
    void patch(
      { subtasks_chain: ids, waiting_on: withAiBuilder(task.waiting_on) },
      {
        waiting_on: [...task.waiting_on.filter((w) => w.target !== 'ai'), { target: 'ai' }],
        subtask_cards: task.subtask_cards.map((s) => ({ ...s, in_chain: ids.includes(s.id) || (s.in_chain && waitsOnAi(s.waiting_on)) })),
      }
    ).then((ok) => ok && toast.success(`The AI builder builds the ${ids.length} picked one at a time, starting with the first`));
  // SF-T-263: with no pick, every open sub-task: a pick left over is cleared.
  const buildOneAtATime = () =>
    void patch(
      { subtasks_chain: [], waiting_on: withAiBuilder(task.waiting_on) },
      {
        waiting_on: [...task.waiting_on.filter((w) => w.target !== 'ai'), { target: 'ai' }],
        subtask_cards: task.subtask_cards.map((s) => ({ ...s, in_chain: false })),
      }
    ).then(
      (ok) => ok && toast.success('The AI builder builds them one at a time, starting with the first')
    );
  const subtaskChoice = (t: TaskCard) => ({ id: t.id, ref: formatRef(t.number), title: t.title });
  const buildSubtasks = (buildIds ?? []).flatMap((id) => task.subtask_cards.filter((s) => s.id === id)).map(subtaskChoice);

  const title = titleDraft?.taskId === task.id ? titleDraft.text : task.title;
  const saveTitle = () => {
    const next = title.trim();
    setTitleDraft(null);
    if (next && next !== task.title) void patch({ title: next }, { title: next });
  };

  const column = meta.columns.find((c) => c.id === task.column_id);
  const columnStatuses = taskColumnStatuses(meta.columns);
  // The Board pop-up on a desktop: two panes, like Trello. The task on the left, its activity
  // and chat on the right, each scrolling on its own. Phones and the Tasks panel: one column.
  const panes = layout === 'wide' && !isMobile;
  // Phones get the pop-up full screen, with the top row pinned as a slim bar holding the close
  // button, so the task can be closed from anywhere in it (T-49).
  const pinnedBar = layout === 'wide' && isMobile;

  const copyLink = () =>
    void copyWithToast(`${window.location.origin}${project ? taskPath(project.slug, task.id) : window.location.pathname}`, 'the link');

  /** The whole task for pasting into an AI chat. */
  const copyMarkdown = () => {
    copyTasksMarkdown([task.id], meta, [task]).then(
      () => toast.success('Markdown copied'),
      () => toast.error('Couldn’t copy the task')
    );
  };

  // Phones: Snooze, Repeat and Copy link go in the More menu, so the top row isn't cramped (T-35).
  const compactToolbar = isMobile;
  // SF-T-79: the PR's actions (Merge, Fix conflicts): beside Run, or in the More menu on phones and in
  // the narrow Tasks panel. SF-T-159: Send to Review and Move to Done are always in the More menu.
  const moveActions: TaskAction[] = planMoveActions({ columnId: task.column_id, columns: meta.columns, mayMove: canEditTasks }).map((plan) => ({
    ...plan,
    onSelect: () => moveTo(plan.columnId!),
  }));
  const actionsInMenu = compactToolbar || layout === 'narrow';
  const canStartRun = Boolean(canRun && project && task.number);
  // SF-T-159: with open sub-tasks, the header builds them all (their questions and moves are theirs);
  // running the task on its own goes in the More menu. SF-T-301: it builds only the ones still to build (no PR, nothing
  // building them). It stays (disabled) while the rest are being built, and once they're all built the header is the usual
  // Run and PR actions. One at a time, it stays while the AI builder has the parent: the chain keeps it until they're done.
  const openSubtasks = hasOpenSubtasks && hydrated ? task.subtask_cards.filter((s) => !isSubtaskDone(s, meta.columns)) : [];
  const toBuildIds = subtasksToBuild(openSubtasks, meta.columns, subtaskRuns).map((s) => s.id);
  const someUnbuilt = openSubtasks.some((s) => !isSubtaskBuilt(s, meta.columns));
  const chainHasIt = Boolean(task.subtasks_one_at_a_time && waitsOnAi(task.waiting_on) && openSubtasks.length);
  const buildsSubtasks = canStartRun && (someUnbuilt || chainHasIt);
  // SF-T-226: in the compact header, the PR action that would work now (Merge, Fix conflicts, Update) takes
  // Run's place, and Run goes in the More menu. Not over Wrap up or Build all, which come first.
  const headerAction = actionsInMenu && !liveRun && !buildsSubtasks ? headerPrAction(prActionList) : null;
  const menuActions: TaskAction[] = [...(actionsInMenu ? prActionList.filter((a) => a !== headerAction) : []), ...moveActions];
  const canSnooze = !column?.is_done && !task.archived;
  // SF-T-207: a top-level task made here, not on a source yet, can be pushed to one.
  const pushSources = meta.push_sources ?? [];
  const mayPushUpstream = canEditTasks && pushSources.length > 0 && !task.upstream && !task.parent && !task.archived && Boolean(task.number);
  // A pushed task whose sub-tasks didn't all go (one failed, past the batch, or added since): push the rest.
  const subtasksSource = task.upstream && !task.parent ? pushSources.find((s) => s.id === task.upstream!.source_id) : undefined;
  const subtasksToPush = hydrated && subtasksSource && canEditTasks && !task.archived ? task.subtask_cards.filter((s) => !s.upstream && !s.archived).length : 0;
  // A sub-task made here whose parent is on a source (one added since, or one that didn't go): under the parent there.
  const parentSource = task.parent?.source_id && !task.upstream ? pushSources.find((s) => s.id === task.parent!.source_id) : undefined;
  const maySubtaskPush = Boolean(parentSource) && canEditTasks && !task.archived;
  const pushToSource = async (source: TaskPushSource) => {
    const res = await pushTaskUpstream(task.id, source.id);
    if (res.error) {
      toast.error(describeApiError(res, `Couldn't push it to ${source.name}`));
      return false;
    }
    const left = res.data?.subtasks_left ?? 0;
    toast.success(res.data?.ref ? `Pushed to ${source.name} as ${res.data.ref}` : `Pushed to ${source.name}`);
    if (left) toast.warning(`${left === 1 ? 'A sub-task' : `${left} sub-tasks`} didn’t go to ${source.name} yet. Push its sub-tasks from the ⋯ menu to send the rest; Settings › Sources says why any failed.`);
    void reload();
    return true;
  };
  const toggleFollowing = () => {
    const following = !task.following;
    // The card's followers too, so the Board's Following view changes at once.
    const others = task.follower_ids.filter((id) => id !== meId);
    const follower_ids = following && meId ? [...others, meId] : others;
    void run(setTaskFollowing(task.id, following), { following, follower_ids });
  };

  // SF-T-196: one line however long the mirrored ref is: it shortens first, and the buttons and the
  // close cross never shrink or leave the screen.
  const toolbar = (
    <div className="flex min-w-0 items-center gap-0.5">
      {/* SF-T-207: the source it's on, by its logo, then (SF-T-176) who it's for, top left. */}
      {task.upstream ? <UpstreamMark upstream={task.upstream} className="mr-1.5" /> : null}
      <TaskRequestor task={task} members={meta.members} size="xs" className="mr-1.5" />
      {column ? (
        <StatusSelect value={column.id} statuses={columnStatuses} onValueChange={moveTo} aria-label="Column" />
      ) : null}
      {task.number ? (
        <span className="ml-2 whitespace-nowrap text-xs tabular-nums text-muted-foreground" data-hint="Task number: search for it, or use it in the API">
          {formatRef(task.number)}
        </span>
      ) : null}
      {/* Mirrored from another board (T-15): where it lives upstream, linked when it has an address. */}
      {task.upstream?.url ? (
        <span className="ml-2 flex min-w-0 text-xs">
          {/* SF-T-196: phones show only its ref (T-301), without the arrow; the hint and the read-out name keep the source. */}
          <ExternalLink
            href={task.upstream.url}
            size="xs"
            data-hint={`Mirrored from ${task.upstream.source_name}`}
            label={compactToolbar && task.upstream.ref ? upstreamBadgeText(task.upstream) : undefined}
            icon={!compactToolbar}
          >
            <span className="truncate">{compactToolbar && task.upstream.ref ? task.upstream.ref : upstreamBadgeText(task.upstream)}</span>
          </ExternalLink>
        </span>
      ) : task.upstream ? (
        <UpstreamChip upstream={task.upstream} className="ml-2" />
      ) : null}
      {/* SF-T-151: the source refused it to the token: not synced until it can see it again. */}
      <BrokenLinkBadge upstream={task.upstream} className="ml-1.5" />
      <span className="flex-1" />
      {runsSlug && agentRun ? (
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label={`Chat with ${runRef(agentRun.number)}'s agent`}
          data-hint="The agent's chat, full screen, as T3 Code's"
          onClick={() => agentChat.open('chat')}
        >
          <MessagesSquare className="size-4" />
          {agentRun.agent_working ? <span className="absolute top-1 right-1 size-2 animate-pulse rounded-full bg-info-strong" aria-hidden /> : null}
        </Button>
      ) : null}
      {runsSlug && agentRun ? (
        <RecordDialog
          open={agentChat.id === 'chat'}
          onClose={agentChat.close}
          // SF-T-266: one thin row: the run (R-12, its status, the machine) is the title; the task, open behind it, is
          // named on a desktop only (a screen reader always hears it).
          title={<AgentRunLine run={agentRun} projectSlug={runsSlug} threadWorking={threadWorkingRun === agentRun.id} />}
          description={<span className="block truncate max-sm:sr-only">{task.number ? `${formatRef(task.number)} ${task.title}` : task.title}</span>}
          // SF-T-266: full screen on a phone; wide and tall on a desktop.
          size="full"
          tall
          errorTitle="Couldn't show the agent's chat"
          recordKey={agentRun.id}
          bodyClassName="flex flex-col overflow-hidden pb-[max(env(safe-area-inset-bottom),1rem)]"
        >
          <div className="flex h-full min-h-0 flex-col">
            <TaskAgentPane
              taskId={task.id}
              runs={runs}
              projectSlug={runsSlug}
              canMessage={canEditTasks}
              fill
              runLine={false}
              onThreadWorking={setThreadWorkingRun}
              onSent={runsLoader.reload}
            />
          </div>
        </RecordDialog>
      ) : null}
      <TaskPrActionButtons actions={actionsInMenu ? (headerAction ? [{ ...headerAction, label: headerActionLabel(headerAction.kind) }] : []) : prActionList} />
      {prActions.dialog}
      {canRun && project && task.number ? (
        <>
          {liveRun ? (
            // SF-T-209: before Build all: a live run started with "Run this task only…" still needs its Wrap up.
            <WrapUpButton
              runId={liveRun.id}
              runNumber={liveRun.number}
              project={project.slug}
              wrapping={Boolean(liveRun.wrap_up_at)}
              suggestionsFirst={liveRun.pause === 'suggestions first'}
              onDone={runsLoader.reload}
            />
          ) : buildsSubtasks && task.subtasks_one_at_a_time ? (
            // SF-T-242: one at a time, the AI builder takes this task and starts the first sub-task.
            <Button
              variant="outline"
              size="xs"
              className="mr-1"
              onClick={buildOneAtATime}
              disabled={waitsOnAi(task.waiting_on)}
              disabledReason="The AI builder has them: each starts once the one before has a PR"
              data-hint="The AI builder builds them in order, one at a time: each its own run and PR, stacked on the one before"
            >
              <Play />
              {compactToolbar ? 'One at a time' : waitsOnAi(task.waiting_on) ? 'With the AI builder' : 'Build one at a time with AI'}
            </Button>
          ) : buildsSubtasks ? (
            <Button
              variant="outline"
              size="xs"
              className="mr-1"
              onClick={() => setBuildIds(toBuildIds)}
              disabled={!toBuildIds.length}
              disabledReason="They're being built now: Run comes back once each has its PR"
              data-hint="One agent builds every open sub-task with no PR yet: one run, one branch, one PR. Each sub-task moves along, and its questions are asked on it"
            >
              <Play />
              {/* Phones: short, so the task's number keeps its line. */}
              {/* SF-T-301: some built already, it builds the rest. */}
              {!toBuildIds.length
                ? compactToolbar
                  ? 'Being built'
                  : 'Sub-tasks being built'
                : toBuildIds.length < openSubtasks.length
                ? compactToolbar
                  ? `Build ${toBuildIds.length} left…`
                  : toBuildIds.length === 1
                    ? 'Build the one left with AI…'
                    : `Build the ${toBuildIds.length} left with AI…`
                : toBuildIds.length === 1
                  ? compactToolbar
                    ? 'Build it…'
                    : 'Build the sub-task with AI…'
                  : compactToolbar
                    ? `Build all ${toBuildIds.length}…`
                    : `Build all ${toBuildIds.length} with AI…`}
            </Button>
          ) : headerAction ? null : (
            <Button
              variant="outline"
              size="xs"
              className="mr-1"
              onClick={() => setRunOpen(true)}
              data-hint="Run a flow on this task on a fleet machine"
            >
              <Play />
              Run
            </Button>
          )}
          <RunTaskDialog
            open={runOpen}
            onOpenChange={setRunOpen}
            taskId={task.id}
            taskRef={formatRef(task.number)}
            taskSize={task.size}
            // SF-T-159: with open sub-tasks this is "Run this task only…", which never carries on a run: a batch's
            // follow-up would take its sub-tasks with it and build them again. SF-T-301: still so once they're all built.
            continueFrom={hasOpenSubtasks ? null : continuableRun(runs, task.prs)}
            project={project.slug}
            onStarted={(started) => {
              setRunOpen(false);
              runStarted(project.slug, started.number);
            }}
          />
        </>
      ) : null}
      {/* Done and archived tasks are off the Board already. */}
      {canSnooze ? (
        <SnoozeMenu
          snoozedUntil={snoozed ? task.snoozed_until : null}
          open={snoozeOpen}
          onOpenChange={setSnoozeOpen}
          onSnooze={snooze}
          backLabel="Back on the Board"
          align="end"
          trigger={
            compactToolbar ? (
              // Phones open it from the More menu; this only places the menu under that button.
              <span aria-hidden className="pointer-events-none -mr-0.5 h-8 w-0" />
            ) : (
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(snoozed && 'text-info-strong')}
                aria-label={snoozed ? `Snoozed until ${snoozeDay(task.snoozed_until!)}` : 'Snooze'}
                data-hint={snoozed ? `Snoozed until ${snoozeDay(task.snoozed_until!)}: click to change or end it` : 'Snooze: off the Board until a day you choose'}
              >
                <AlarmClock className="size-4" />
              </Button>
            )
          }
        />
      ) : null}
      {compactToolbar ? null : (
        <Button
          variant="ghost"
          size="icon-sm"
          className={cn(task.repeat && 'text-info-strong')}
          aria-label={repeatText ? `Repeats: ${repeatText}` : 'Repeat'}
          data-hint={repeatText ? `${repeatText}: click to change or stop it` : 'Repeat: bring it back every week, month…'}
          onClick={() => setRepeatOpen(true)}
        >
          <Repeat className="size-4" />
        </Button>
      )}
      <TaskRepeatDialog open={repeatOpen} onOpenChange={setRepeatOpen} repeat={task.repeat} onSave={saveRepeat} />
      <PushUpstreamDialog
        open={pushOpen}
        onOpenChange={setPushOpen}
        sources={pushSources}
        subtasks={hydrated ? task.subtask_cards.filter((s) => !s.upstream).length : 0}
        onPush={pushToSource}
      />
      <MakeSubtaskDialog
        open={makeSubOpen}
        onOpenChange={setMakeSubOpen}
        taskId={task.id}
        onPick={(parent) =>
          run(makeTaskSubtaskOf(task.id, parent.id), {
            parent: { task_id: parent.id, number: parent.number, title: parent.title },
            show_on_board: false,
          }).then((ok) => {
            if (ok) toast.success(`It’s a sub-task of ${formatRef(parent.number)} now`);
            return ok;
          })
        }
      />
      {/* SF-T-196: phones follow from the More menu, so the close cross keeps its room. */}
      {compactToolbar ? null : (
        <Button
          variant="ghost"
          size="icon-sm"
          className={cn(task.following && 'text-primary')}
          aria-label={task.following ? 'Unfollow' : 'Follow'}
          data-hint={task.following ? 'Following: you get notified. Click to stop.' : 'Follow to get notified'}
          onClick={toggleFollowing}
        >
          {task.following ? <BellRing className="size-4" /> : <BellOff className="size-4" />}
        </Button>
      )}
      {compactToolbar ? null : (
        <Button variant="ghost" size="icon-sm" aria-label="Copy link" data-hint="Copy link" onClick={copyLink}>
          <Link2 className="size-4" />
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="More actions">
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {buildsSubtasks && !liveRun ? (
            // SF-T-159: the task on its own, without its sub-tasks. After this menu has closed, so the dialog keeps the focus.
            <DropdownMenuItem onSelect={() => setTimeout(() => setRunOpen(true))}>
              <Play className="size-4" />
              <span className="flex min-w-0 flex-col">
                <span>Run this task only…</span>
                <span className="text-2xs text-muted-foreground">Without its sub-tasks</span>
              </span>
            </DropdownMenuItem>
          ) : headerAction && canStartRun ? (
            // SF-T-226: the header shows the PR's next step instead. After this menu has closed, so the dialog keeps the focus.
            <DropdownMenuItem onSelect={() => setTimeout(() => setRunOpen(true))}>
              <Play className="size-4" />
              Run…
            </DropdownMenuItem>
          ) : null}
          <TaskPrActionMenuItems actions={menuActions} />
          {compactToolbar ? (
            <>
              {canSnooze ? (
                // After this menu has closed, or its closing takes the focus back from the Snooze menu.
                <DropdownMenuItem onSelect={() => setTimeout(() => setSnoozeOpen(true))}>
                  <AlarmClock className={cn('size-4', snoozed && 'text-info-strong')} />
                  {snoozed ? `Snoozed until ${snoozeDay(task.snoozed_until!)}` : 'Snooze…'}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onSelect={() => setRepeatOpen(true)}>
                <Repeat className={cn('size-4', task.repeat && 'text-info-strong')} />
                {repeatText ? `Repeats: ${repeatText}` : 'Repeat…'}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={copyLink}>
                <Link2 className="size-4" />
                Copy link
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={toggleFollowing}>
                {task.following ? <BellRing className="size-4 text-primary" /> : <BellOff className="size-4" />}
                {task.following ? 'Unfollow' : 'Follow'}
              </DropdownMenuItem>
            </>
          ) : null}
          {isAdmin ? (
            <DropdownMenuItem onSelect={() => chatAboutTask({ id: task.id, ref: formatRef(task.number), title: task.title, projectId: project?.id ?? null })}>
              <MessagesSquare className="size-4" />
              Chat about this task
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={copyMarkdown}>
            <ClipboardCopy className="size-4" />
            Copy Markdown
          </DropdownMenuItem>
          {!task.parent && canEditTasks && hydrated && !task.subtask_cards.length && task.number ? (
            // SF-T-132: the way back from "Make it a task of its own" (one level: not for a task with sub-tasks).
            <DropdownMenuItem onSelect={() => setTimeout(() => setMakeSubOpen(true))}>
              <ListTree className="size-4" />
              Make it a sub-task of…
            </DropdownMenuItem>
          ) : null}
          {task.parent && canEditTasks ? (
            // SF-T-147: leaves its parent for the Board, keeping everything else.
            <DropdownMenuItem
              onSelect={() =>
                void run(promoteSubtaskToTask(task.id), { parent: null, show_on_board: false }).then(
                  (ok) => ok && toast.success('It’s a task of its own now, on the Board')
                )
              }
            >
              <ArrowUpFromLine className="size-4" />
              Make it a task of its own
            </DropdownMenuItem>
          ) : null}
          {maySubtaskPush && parentSource ? (
            <DropdownMenuItem onSelect={() => void pushToSource(parentSource)} data-hint={`Make it under its parent task on ${parentSource.name}, and mirror it from there`}>
              <ArrowUpFromLine className="size-4" />
              Push to {TASK_SOURCE_LABELS[parentSource.kind]}
            </DropdownMenuItem>
          ) : null}
          {subtasksToPush && subtasksSource ? (
            <DropdownMenuItem onSelect={() => void pushToSource(subtasksSource)}>
              <ArrowUpFromLine className="size-4" />
              Push {subtasksToPush === 1 ? 'its sub-task' : `its ${subtasksToPush} sub-tasks`} to {TASK_SOURCE_LABELS[subtasksSource.kind]}
            </DropdownMenuItem>
          ) : null}
          {mayPushUpstream ? (
            // SF-T-207: a task made here goes onto one of the project's sources, mirrored from there after.
            <DropdownMenuItem onSelect={() => setTimeout(() => setPushOpen(true))}>
              <ArrowUpFromLine className="size-4" />
              Push to source…
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            onSelect={() => void patch({ archived: !task.archived }, { archived: !task.archived, ...(task.archived ? {} : { snoozed_until: null }) })}
          >
            {task.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
            {task.archived ? 'Put back on the Board' : 'Archive'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {pinnedBar ? (
        // The pop-up hides its own close button while this bar is there (TaskPopup): this one stays on screen.
        <DialogClose asChild>
          <Button variant="ghost" size="icon-sm" className="size-11" aria-label="Close">
            <X className="size-4" />
          </Button>
        </DialogClose>
      ) : null}
    </div>
  );

  const titleField = (
    // A one-line textarea so long titles wrap; Enter still saves.
    <Textarea
      ref={pinnedBar ? watchTitle : undefined}
      value={title}
      rows={1}
      // A mirrored task's title is changed upstream (T-15).
      readOnly={Boolean(task.upstream)}
      data-hint={task.upstream ? `Change the title in ${TASK_SOURCE_LABELS[task.upstream.kind]}` : undefined}
      onChange={(e) => setTitleDraft({ taskId: task.id, text: e.target.value.replace(/\n/g, ' ') })}
      onBlur={saveTitle}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === 'Escape') setTitleDraft(null);
      }}
      aria-label="Task title"
      className="-ml-2 min-h-0 resize-none border-transparent px-2 py-1 text-xl font-semibold leading-snug shadow-none hover:border-input focus-visible:border-input md:text-xl"
    />
  );

  const summarise = async () => {
    setSummarising(true);
    const res = await summariseTask(task.id);
    setSummarising(false);
    if (res.error) toast.error(describeApiError(res, "Couldn't summarise the task"));
    else void reload();
  };

  // T-17: what the task is and where it stands, for someone catching up. Under the task's fields, above
  // what's to answer and the description (SF-T-305: in the pop-up too, where it was over the activity).
  // In one column (phones, the Tasks panel) only once there is one.
  const summary = (
    <TaskAiSummary
      summary={task.ai_summary}
      dueAt={task.ai_summary_due_at}
      onSummarise={() => void summarise()}
      summarising={summarising}
    />
  );

  const timeline = (
    <TaskTimeline
      task={task}
      hydrated={hydrated}
      showComposer={!isMobile}
      fill={panes}
      header={!panes && !runsSlug}
      members={meta.members}
      labels={meta.labels}
      runs={runsSlug ? { projectSlug: runsSlug, byNumber: runsByNumber(runs ?? []) } : undefined}
      onNavigate={onNavigate}
      commentSend={commentSend}
    />
  );

  // SF-T-209: the activity pane's two tabs: Comments (the timeline and its box) and Agent (the run's thread).
  // SF-T-305: Show changes sits at the end of the tabs' row (Comments only: it doesn't touch the Agent tab), and
  // the timeline has no heading of its own; in the pop-up that row is level with the close button. In one column
  // with no tabs, the timeline keeps its Activity heading and the toggle beside it.
  const showChanges = (panes || runsSlug) && (!runsSlug || shownTab === 'comments') ? <ShowChangesToggle className="ml-auto" /> : null;
  const activity = runsSlug ? (
    <Tabs
      value={shownTab}
      onValueChange={(v) => setActivityTab({ taskId, tab: v === 'agent' ? 'agent' : 'comments' })}
      className={panes ? 'flex min-h-0 flex-1 flex-col gap-3' : 'gap-3'}
    >
      <div className={cn('flex items-center gap-3', panes && 'h-8 shrink-0 pr-8')}>
        <TabsList size="sm" aria-label="Activity">
          <TabsTrigger value="comments">Comments</TabsTrigger>
          <TabsTrigger value="agent">Agent</TabsTrigger>
        </TabsList>
        {showChanges}
      </div>
      <TabsContent value="comments" className={panes ? 'min-h-0 flex-1' : undefined}>
        {timeline}
      </TabsContent>
      <TabsContent value="agent" className={panes ? 'min-h-0 flex-1' : undefined}>
        <TaskAgentPane
          taskId={task.id}
          runs={runs}
          projectSlug={runsSlug}
          canMessage={canEditTasks}
          fill={panes}
          pinned={isMobile}
          onSent={runsLoader.reload}
        />
      </TabsContent>
    </Tabs>
  ) : panes ? (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex h-8 shrink-0 items-center gap-3 pr-8">{showChanges}</div>
      <div className="min-h-0 flex-1">{timeline}</div>
    </div>
  ) : (
    timeline
  );

  const view = (
    // Any file on the task, in the Attachments list or the chat, opens in one viewer.
    <TaskAttachmentViewerProvider task={task} members={meta.members} onNavigate={onNavigate}>
      {/* data-task-view: pasting a file anywhere in here attaches it to this task (see TaskChatComposer).
          min-w-0: in the pop-up's grid, a long chip must not widen the whole task past the screen. */}
      <div
        className={cn(
          'group/task relative min-w-0',
          panes ? 'grid h-full min-h-0' : 'space-y-5',
          // SF-T-222: where something pins under the phone's bar (the Agent tab's header): the bar (PINNED_BAR_HEIGHT,
          // 45px) and the title under it once scrolled (29px), less the pop-up's padding the bar reaches past.
          pinnedBar && '[--task-sticky-top:calc(74px-1rem)] sm:[--task-sticky-top:calc(74px-1.5rem)]'
        )}
        // A narrower window than when it was saved still leaves the task its minimum.
        style={panes ? { gridTemplateColumns: `minmax(0,1fr) max(${ACTIVITY_MIN_WIDTH}px, min(${activityWidth}px, 100% - ${TASK_MIN_WIDTH}px))` } : undefined}
        data-task-view={task.id}
        {...drop.bind}
      >
        {drop.dragging ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-background/80 text-sm font-medium text-primary">
            Drop to add to this task
          </div>
        ) : null}

        <div className={cn('min-w-0 space-y-5', panes && 'overflow-y-auto p-6')}>
          {pinnedBar ? (
            <>
              {/* Phones (T-49): the top row stays pinned while the task scrolls under it. The pop-up
                  scrolls it in a p-4 box (p-6 from sm): the negative top and margins reach past that
                  padding, like the chat box at the bottom. */}
              <div
                className="sticky -top-4 z-20 -mx-4 -mt-4 border-b bg-background px-4 sm:-top-6 sm:-mx-6 sm:-mt-6 sm:px-6"
                style={{ minHeight: PINNED_BAR_HEIGHT }}
                data-pinned-bar
              >
                {toolbar}
                {titleOutOfView ? (
                  // Over the task, not in the flow, so showing it never moves what's under it. Only a
                  // reminder of the title, not a control: a tap target would need 44px of height.
                  <p
                    aria-hidden
                    className="absolute inset-x-0 top-full truncate border-b bg-background px-4 py-1 text-sm font-semibold sm:px-6"
                  >
                    {title}
                  </p>
                ) : null}
              </div>
              {/* -mt-4: the title sits just under the bar, as it does under the row elsewhere. */}
              <header className="-mt-4">{titleField}</header>
            </>
          ) : (
            // The controls get their own row, so the title has the full width to wrap in.
            <header className="space-y-1">
              {toolbar}
              {titleField}
            </header>
          )}

          {task.archived ? (
            <AppAlert tone="neutral" compact role="status" icon={<Archive />}>
              This task is archived, so it isn’t on the Board.
            </AppAlert>
          ) : snoozed ? (
            <AppAlert
              tone="info"
              compact
              role="status"
              icon={<AlarmClock />}
              action={
                <Button variant="outline" size="xs" onClick={() => snooze(null)}>
                  Unsnooze
                </Button>
              }
            >
              Snoozed until {snoozeDay(task.snoozed_until!)}, so it isn’t on the Board.
            </AppAlert>
          ) : null}
          {/* T-27: the AI builder's own draft. Nothing builds it until a person promotes it (or asks the AI builder, or runs it). */}
          {task.draft && !task.archived ? (
            <AppAlert
              tone="warning"
              compact
              role="status"
              icon={<Bot />}
              title="Draft from the AI builder"
              action={
                // People who can't edit tasks see the notice, not the buttons.
                canEditTasks && (
                <div className="flex gap-2">
                  <Button variant="outline" size="xs" onClick={() => void patch({ draft: false }, { draft: false })}>
                    Promote
                  </Button>
                  <Button variant="ghost" size="xs" onClick={() => void patch({ archived: true }, { archived: true, snoozed_until: null })}>
                    Discard
                  </Button>
                </div>
                )
              }
            >
              The AI builder made this task when it was asked to. It isn’t built until someone promotes it: Promote, ask the AI builder to build it, or press Run. Discard archives it.
            </AppAlert>
          ) : null}
          {/* A stacked task (T-14) whose base PR is merged: its own PR should move to the default branch. */}
          {task.stacked_on?.pr_merged ? (
            <AppAlert tone="warning" compact role="status" icon={<GitMerge />}>
              {stackedMergedText(task.stacked_on, project?.task_prefix)}
            </AppAlert>
          ) : null}
          {task.repeat ? (
            <AppAlert
              tone="info"
              compact
              role="status"
              icon={<Repeat />}
              action={
                <Button variant="outline" size="xs" onClick={() => setRepeatOpen(true)}>
                  Change
                </Button>
              }
            >
              <span>
                {repeatText}
                {task.repeat.new_task ? ', as a new task' : null}
                {task.repeat_next_at ? <>. Next back {repeatNextLabel(task.repeat_next_at)}</> : null}
              </span>
            </AppAlert>
          ) : null}

          <TaskProperties
            task={task}
            meta={meta}
            patch={patch}
            run={run}
            currentPage={currentPage}
            onNavigate={onNavigate}
            inline={panes}
          />
          {panes || task.ai_summary ? summary : null}
          {/* T-72: the AI builder's open questions and undecided suggestions, first thing under the fields. */}
          <TaskToAnswer
            taskId={task.id}
            events={task.events}
            members={meta.members}
            onAnswer={(message, eventId) => sendMessage(task.id, message, [], undefined, eventId)}
            onNavigate={onNavigate}
            // T-58: on a mirrored task, sent items say so and Send to <source> sends them there.
            upstream={{ questions: upstreamDecisions('questions'), suggestions: upstreamDecisions('suggestions') }}
            // SF-T-304: Ask on a suggestion is a live chat with an agent in the task's worktree.
            suggestionChats={{ canAsk: canEditTasks }}
            // SF-T-74: a disputed review's findings, settled here (only for people who can edit tasks).
            onSettle={
              canEditTasks
                ? async (eventId, picks) => {
                    const res = await settleTaskDispute(task.id, eventId, picks);
                    if (!res.data) {
                      toast.error(describeApiError(res, "Couldn't settle the review"));
                      return false;
                    }
                    toast.success(picks.some((p) => sendsBack(p.choice)) ? 'Settled: sent back to the builder' : 'Settled');
                    void reload();
                    return true;
                  }
                : undefined
            }
            // SF-T-178: Dismiss says No thanks to a run's suggestions still open.
            onDismiss={
              canEditTasks
                ? async (eventId) => {
                    const res = await declineTaskSuggestions(task.id, [eventId], undefined, 'task');
                    if (!res.data) {
                      toast.error(describeApiError(res, "Couldn't dismiss the suggestions"));
                      return false;
                    }
                    toast.success(res.data.declined === 1 ? 'No thanks on 1 suggestion' : `No thanks on ${res.data.declined} suggestions`);
                    void reload();
                    return true;
                  }
                : undefined
            }
            // SF-T-178: sub-tasks the AI builder proposed, added (or all rejected) here in one go; none on a sub-task (one level).
            proposals={!task.parent}
            onAddSubtasks={
              canEditTasks && !task.parent && task.number && hydrated
                ? async (eventId, numbers) => {
                    const res = await addProposedSubtasks(task.id, eventId, numbers);
                    if (!res.data) {
                      toast.error(describeApiError(res, "Couldn't add the sub-tasks"));
                      return false;
                    }
                    const made = res.data.tasks.map((t) => t.ref);
                    toast.success(made.length ? `Added ${made.join(', ')}` : 'Rejected the proposed sub-tasks');
                    void reload();
                    return true;
                  }
                : undefined
            }
            onApply={async (eventId, decisions) => {
              const res = await decideTaskSuggestions(task.id, eventId, decisions);
              if (!res.data) {
                toast.error(describeApiError(res, "Couldn't apply your decisions"));
                return false;
              }
              const made = res.data.tasks.map((t) => t.ref);
              toast.success(made.length ? `Decisions saved; made ${made.join(', ')}` : 'Decisions saved');
              void reload();
              return true;
            }}
          />
          <TaskDescription
            key={task.id}
            task={task}
            hydrated={hydrated}
            members={meta.members}
            saveDescription={saveDescription}
            onReload={reload}
            onNavigate={onNavigate}
          />
          {/* SF-T-305: with neither a spec nor sub-tasks, "Add a spec" and "Add sub-tasks" share a row; an open
              section (a <section>) takes the whole width, so the other link wraps under it. */}
          <div className="flex flex-wrap items-center gap-x-1 gap-y-5 empty:hidden [&>section]:w-full">
            {/* T-59: the spec feedback runs write, beside the description (which they leave alone). */}
            {task.number ? (
              <TaskSpec
                key={`spec-${task.id}`}
                task={task}
                hydrated={hydrated}
                members={meta.members}
                canEdit={canEditTasks}
                saveSpec={saveDescription}
                onReload={reload}
                onNavigate={onNavigate}
              />
            ) : null}
            {/* Sub-tasks (T-42): one level, so a sub-task has none. Shown once the task is saved and loaded. */}
            {!task.parent && task.number && hydrated ? (
              <TaskActiveRunsContext.Provider value={subtaskRuns}>
                <TaskSubtasks
                  // A new task starts with its own box: what was typed on another task (and Add sub-tasks opened there) doesn't carry over.
                  key={task.id}
                  subtasks={task.subtask_cards}
                  columns={meta.columns}
                  members={meta.members}
                  labels={meta.labels}
                  types={meta.types}
                  canRun={canRun && Boolean(project)}
                  pending={pendingSubtasks.rows}
                  onAdd={pendingSubtasks.add}
                  onRetryPending={pendingSubtasks.retry}
                  onRemovePending={pendingSubtasks.remove}
                  onToggleDone={toggleSubtaskDone}
                  onHandToAi={handSubtaskToAi}
                  onToggleBoard={toggleSubtaskBoard}
                  onBuild={setBuildIds}
                  onReorder={reorderSubtasks}
                  onPromote={canEditTasks ? promoteSubtask : undefined}
                  build={{
                    subtasks_one_at_a_time: task.subtasks_one_at_a_time,
                    subtasks_defer_questions: task.subtasks_defer_questions,
                    subtasks_concurrency: task.subtasks_concurrency,
                  }}
                  onChangeBuild={changeSubtaskBuild}
                  onBuildOneAtATime={buildOneAtATime}
                  onPickChain={pickChain}
                  onClearChain={clearChain}
                  onChangeAfter={canEditTasks ? changeSubtaskAfter : undefined}
                  onToggleBreak={canEditTasks ? toggleSubtaskBreak : undefined}
                  chainStopped={Boolean(task.subtasks_chain_stopped_for)}
                  onContinue={canRun && project ? continueChain : undefined}
                  suggestion={task.subtasks_suggestion}
                  suggestingOrder={suggestingOrder}
                  onSuggestOrder={canEditTasks ? () => void suggestOrder() : undefined}
                  onAcceptOrder={canEditTasks ? acceptOrder : undefined}
                  onDismissOrder={canEditTasks ? dismissOrder : undefined}
                  buildingOneAtATime={waitsOnAi(task.waiting_on)}
                />
              </TaskActiveRunsContext.Provider>
            ) : null}
          </div>
          {canRun && project && buildSubtasks.length ? (
            <RunTaskDialog
              open
              onOpenChange={(open) => !open && setBuildIds(null)}
              taskId={task.id}
              taskRef={formatRef(task.number)}
              taskSize={task.size}
              subtasks={buildSubtasks}
              stackChoices={stackableSubtasks(task.subtask_cards).map(subtaskChoice)}
              project={project.slug}
              onStarted={(started) => {
                setBuildIds(null);
                runStarted(project.slug, started.number);
              }}
            />
          ) : null}
          {/* The AI builder's answered questions (T-22) and decided suggestions (T-14), each folded to a line (T-72). */}
          <TaskDecisions taskId={task.id} events={task.events} members={meta.members} />
          <TaskSuggestions taskId={task.id} events={task.events} onNavigate={onNavigate} canStop={canEditTasks} />
          {/* SF-T-178: the AI builder's decided sub-task proposals, folded the same way (a sub-task lists its undecided ones too). */}
          {task.number && hydrated ? <ProposedSubtasks taskId={task.id} events={task.events} all={Boolean(task.parent)} /> : null}
          {/* SF-T-177: permission requests the run's agent waits on in T3 Code: approve there, or deny here. */}
          <TaskApprovalRequestsSection taskId={task.id} version={`${task.updated_at}:${task.pending_approvals}`} />
          {/* T-57: the AI builder's patch requests (changes the guards keep it from making), each opening its dialog. */}
          <TaskPatchRequestsSection taskId={task.id} version={`${task.updated_at}:${task.pending_patches}`} pending={task.pending_patches} />
          <TaskAttachments task={task} canDelete={canEditTasks} />
          {runsSlug ? (
            <section className="space-y-2" aria-label="Runs">
              <h3 className="text-sm font-semibold">Runs</h3>
              <TaskRunsList
                projectSlug={runsSlug}
                runs={runs}
                error={runsError}
                onRetry={runsLoader.reload}
                onNavigate={onNavigate}
                highlight={startedRun}
                onRetrySetup={
                  canRun
                    ? async (failed) => {
                        const res = await retryRunSetup(failed.id, { project: runsSlug });
                        if (!res.data) {
                          toast.error(describeApiError(res, "Couldn't retry the setup"));
                          runsLoader.reload();
                          return;
                        }
                        runStarted(runsSlug, res.data.run.number);
                      }
                    : undefined
                }
                // SF-T-279: its open questions and waits change with it, so the task reloads too.
                onClosed={
                  canRun
                    ? () => {
                        runsLoader.reload();
                        void reload();
                      }
                    : undefined
                }
              />
            </section>
          ) : null}
          {runsSlug && (devServers.devServers || devServers.error) ? (
            // SF-T-209: the worktree's dev servers, to try the work: links, Start, Stop, Tidy and the idle time.
            // SF-T-305: the panel puts the heading in its line when there are no servers, over its box otherwise.
            <section className="space-y-2" aria-label="Dev servers">
              {devServers.devServers ? (
                <>
                  <TaskDevServersPanel
                    heading="Dev servers"
                    devServers={devServers.devServers}
                    canStart={canEditTasks}
                    canStop={canRun}
                    onChanged={devServers.reload}
                    project={runsSlug}
                  />
                  {devServers.error ? <LoadError compact title="Couldn't refresh the dev servers" error={devServers.error} onRetry={devServers.reload} /> : null}
                </>
              ) : (
                <>
                <h3 className="text-sm font-semibold">Dev servers</h3>
                <LoadError compact title="Couldn't read the dev servers" error={devServers.error ?? ''} onRetry={devServers.reload} />
                </>
              )}
            </section>
          ) : null}
          {runsSlug ? (
            // T-12, T-102: what the agents and the people spent on it, in one card (SF-T-305).
            <section className="space-y-2" aria-label="Usage">
              <h3 className="text-sm font-semibold">Usage</h3>
              <TaskUsageCard usage={usage.usage} people={usage.people} error={usage.error} onRetry={usage.reload} projectSlug={runsSlug} onNavigate={onNavigate} />
            </section>
          ) : null}
          {panes ? null : <div ref={commentsEnd}>{activity}</div>}
        </div>

        {panes ? (
          // pt-2: the tabs' row sits level with the pop-up's close button (pr-8 on the row keeps clear of it).
          <div className="@container/activity relative flex min-h-0 flex-col border-l bg-muted/30 px-4 pb-4 pt-2">
            {/* Drag the border to widen the chat for a long conversation. */}
            <PaneResizeHandle
              width={activityWidth}
              min={ACTIVITY_MIN_WIDTH}
              max={(handle) => (handle.parentElement?.parentElement?.clientWidth ?? window.innerWidth) - TASK_MIN_WIDTH}
              grows="left"
              onResize={setDraggedActivityWidth}
              onResizeEnd={(width) => {
                saveActivityWidth(width);
                setDraggedActivityWidth(null);
              }}
              label="Resize the activity pane"
              className="absolute inset-y-0 -left-2 z-10"
            />
            {activity}
          </div>
        ) : null}

        {isMobile && shownTab === 'comments' ? (
          // Phones: the chat box stays pinned to the bottom of the screen while you scroll the task.
          // The pop-up and the Tasks panel both scroll it in a p-4 box: -bottom-4 reaches past that
          // padding while scrolling, and -mb-4 once scrolled to the end, so it sits flush (T-35). While
          // you type in the task's other boxes (the description, the title, a new sub-task: SF-T-133) it stays at the end instead,
          // so it doesn't cover them in the little room above the keyboard (T-47). Done in CSS, so it
          // pins again however the box closes (Esc takes it away without a blur).
          <div
            data-task-chat-box
            className="sticky -bottom-4 z-10 -mb-4 border-t bg-background pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3 group-has-[:is(textarea,input):focus:not([data-task-chat-box]_*)]/task:relative"
          >
            {commentsJump.away ? <JumpToLatestButton className="bottom-full mb-2" onClick={commentsJump.jump} /> : null}
            <TaskChatComposer taskId={task.id} members={meta.members} upstream={task.upstream} />
          </div>
        ) : null}
      </div>
    </TaskAttachmentViewerProvider>
  );
  // Everyone on the project can @AI builder in a comment: it carries on the task's thread, or starts it (T-21).
  return <AiBuilderMentions.Provider value>{view}</AiBuilderMentions.Provider>;
}

/** The highest run number the timeline names ("Factory · R-3"), so a run started elsewhere loads. */
function newestRunMentioned(events: TaskDetail['events'] | undefined): number | null {
  let newest: number | null = null;
  for (const event of events ?? []) {
    const number = runNumberFromActorLabel(event.actor_label);
    if (number !== null && (newest === null || number > newest)) newest = number;
  }
  return newest;
}
